import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    // Tabs
    "tabPleine", "tabNue",
    // Inputs
    "scpiSelector", "scpiPreloaded",
    "amountSlider", "amountInput", "amountDisplay",
    "rateSlider", "rateInput", "rateDisplay", "rateContainer", "rateNaTooltip",
    "durationSlider", "durationInput", "durationDisplay", "durationDatalist", "durationMinLabel", "durationMaxLabel",
    "durationInvestContainer",
    "revalSlider", "revalInput", "revalDisplay",
    // Credit toggle and inputs
    "creditToggle", "creditSection",
    "apportSlider", "apportInput", "apportDisplay", "apportMaxLabel",
    "loanDurationSlider", "loanDurationInput", "loanDurationDisplay",
    "loanRateSlider", "loanRateInput", "loanRateDisplay",
    // Nue-propriété inputs
    "nueSection", "npDurationTemplate", "npDurationsContainer",
    "npDurationSliderContainer", "npDurationSlider", "npDurationInput", "npDurationDisplay",
    "npDurationMinLabel", "npDurationMaxLabel",
    // Warning message
    "ineligibleWarning", "ineligibleMessage",
    // Results pleine-propriété
    "results", "incomeLabel", "monthlyIncome", "totalIncome", "finalValue",
    "capitalGain", "totalGain", "totalGainLabel", "irrDisplay", "irrCard",
    // Results nue-propriété
    "nueResults", "nueSharePrice", "nueRate", "nueInitialAmount",
    "nueFinalValue", "nueGain", "nueIrr",
    // Credit results
    "creditInfo", "loanAmount", "monthlyPayment", "creditCost",
    // Layout sections
    "slidersSection", "actionButtons", "exportButton",
    // Warning modal
    "warningModal"
  ]

  static values = {
    scpis: Array,
    mode: { type: String, default: "pleine" },
    defaultTab: { type: String, default: "pleine" },
    lockedCredit: { type: Boolean, default: false }
  }

  connect() {
    // Check if SCPI is preloaded (mode SCPI)
    this.isScpiMode = this.hasScpiPreloadedTarget
    this.currentEnjoymentDelay = 6 // Default: 6 months (most common)
    this.currentPaymentFrequency = "Mensuelle" // Default: monthly payment
    this.lastSelectedScpiId = null // Store last selected SCPI for persistence
    this.rateNotAvailable = false // Track if rate is N/A for current SCPI

    if (this.isScpiMode) {
      const preloaded = this.scpiPreloadedTarget
      const rate = parseFloat(preloaded.dataset.distributionRate)

      // If distribution rate is not available, lock slider and show N/A indicator
      if (isNaN(rate) || !preloaded.dataset.distributionRate) {
        // Use target_irr if available, otherwise default to 7%
        const targetIrr = parseFloat(preloaded.dataset.targetIrr)
        const defaultRate = isNaN(targetIrr) ? 7.0 : targetIrr
        this.rateNotAvailable = true
        this.rateSliderTarget.value = defaultRate
        this.rateInputTarget.value = defaultRate
        this.rateDisplayTarget.textContent = defaultRate.toFixed(2).replace('.', ',') + ' %'
        // Show tooltip for N/A indicator
        if (this.hasRateNaTooltipTarget) {
          this.rateNaTooltipTarget.classList.remove('hidden')
        }
        this.setRateSliderState(true) // Lock slider
      } else {
        this.rateNotAvailable = false
        this.rateSliderTarget.value = rate
        this.rateInputTarget.value = rate
        this.rateDisplayTarget.textContent = rate.toFixed(2).replace('.', ',') + ' %'
        // Hide tooltip for N/A indicator
        if (this.hasRateNaTooltipTarget) {
          this.rateNaTooltipTarget.classList.add('hidden')
        }
        this.setRateSliderState(true)
      }

      // Update income label and enjoyment delay for preloaded SCPI
      const scpi = this.scpisValue[0] // In SCPI mode, there's only one SCPI
      if (scpi) {
        // Store payment frequency
        this.currentPaymentFrequency = scpi.payment_frequency || "Mensuelle"

        if (scpi.payment_frequency === "Trimestrielle") {
          this.incomeLabelTarget.textContent = "Acompte sur distribution trimestriel potentiel"
        } else {
          this.incomeLabelTarget.textContent = "Acompte sur distribution mensuel potentiel"
        }
        // Store enjoyment delay (+1 month as per client requirements)
        this.currentEnjoymentDelay = (parseInt(scpi.enjoyment_delay) || 0) + 1
        // Load bare ownership durations
        this.loadBareOwnershipDurations(scpi)
      }
    } else {
      // In full mode, load default durations if no SCPI selected
      if (this.hasNpDurationsContainerTarget) {
        this.loadBareOwnershipDurations({
          bare_ownership_durations: [5, 10, 15, 20],
          bare_ownership_possible: true
        })
      }
    }

    // Set initial mode from defaultTab value
    this.modeValue = this.defaultTabValue
    this.applyTabStyles()

    // If locked credit mode, force credit toggle to be checked and show credit section
    if (this.lockedCreditValue) {
      this.creditToggleTarget.checked = true
      this.creditSectionTarget.classList.remove('hidden')
      if (this.hasCreditInfoTarget) {
        this.creditInfoTarget.classList.remove('hidden')
      }
    }

    // Check if a SCPI is already selected at page load and load its data
    if (!this.isScpiMode && this.hasScpiSelectorTarget) {
      const selectedScpiId = this.scpiSelectorTarget.value
      if (selectedScpiId !== "") {
        // A SCPI is selected, load its data (rate, delay, etc.)
        this.selectScpi()
      }
    }

    // "Paramètres personnalisés" option is now always enabled
    // this.disableCustomParametersOption() // Removed to allow re-selection

    // Read and sync all slider values to their displays at page load
    // This ensures calculations use the actual slider positions (browser may preserve them)

    // Amount
    if (this.hasAmountDisplayTarget && this.hasAmountSliderTarget) {
      const amount = parseInt(this.amountSliderTarget.value) || 100000
      this.amountInputTarget.value = amount
      this.amountDisplayTarget.value = this.formatNumber(amount)
    }

    // Rate
    if (this.hasRateDisplayTarget && this.hasRateSliderTarget) {
      const rate = parseFloat(this.rateSliderTarget.value) || 7.0
      this.rateInputTarget.value = rate
      this.rateDisplayTarget.textContent = rate.toFixed(2).replace('.', ',') + ' %'
    }

    // Duration
    if (this.hasDurationDisplayTarget && this.hasDurationSliderTarget) {
      const duration = parseInt(this.durationSliderTarget.value) || 10
      this.durationInputTarget.value = duration
      this.durationDisplayTarget.textContent = duration
    }

    // Revalorisation
    if (this.hasRevalDisplayTarget && this.hasRevalSliderTarget) {
      const reval = parseFloat(this.revalSliderTarget.value) || 0
      this.revalInputTarget.value = reval
      this.revalDisplayTarget.textContent = reval.toFixed(1).replace('.', ',')
    }

    // Initialize apport max based on current investment amount
    if (this.hasAmountSliderTarget) {
      const amount = parseInt(this.amountSliderTarget.value) || 100000
      this.updateApportMax(amount)
    }

    // Set initial export button state (disabled if no real SCPI selected at page load)
    this.updateExportButtonState()

    this.calculate()
  }

  // Tab switching
  selectMode(event) {
    const mode = event.currentTarget.dataset.mode
    this.modeValue = mode
    this.applyTabStyles()

    // Handle "Paramètres personnalisés" option based on mode
    this.updateParametresPersonnalisesOption(mode)

    // Check if we need to disable inputs
    if (mode === 'nue') {
      // In nue mode: disable if no SCPI selected OR if SCPI not available for bare ownership
      // Only check scpiSelector if it exists (not in SCPI mode)
      const noScpiSelected = this.hasScpiSelectorTarget && this.scpiSelectorTarget.value === ""
      if (noScpiSelected || this.currentBareOwnershipPossible === false) {
        this.disableAllInputs(true)
        if (noScpiSelected) {
          this.showPlaceholderResults()
        }
      } else {
        this.disableAllInputs(false)
      }
    } else {
      // In pleine mode: always enable and remove blur from all sections
      this.disableAllInputs(false)
      if (this.hasResultsTarget) {
        this.resultsTarget.classList.remove('opacity-50', 'blur-sm')
      }
      if (this.hasNueResultsTarget) {
        this.nueResultsTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
      }
      if (this.hasNueSectionTarget) {
        this.nueSectionTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
      }
      if (this.hasSlidersSectionTarget) {
        this.slidersSectionTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
      }
      if (this.hasActionButtonsTarget) {
        this.actionButtonsTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
      }
      // Hide ineligible warning when switching to pleine mode
      this.hideIneligibleWarning()
    }

    // No more auto-adjustment - user can freely choose duration >= démembrement

    // Refresh export button state (mode switch can change SCPI selection)
    this.updateExportButtonState()

    this.calculate()
  }

  // Disable custom parameters option permanently
  disableCustomParametersOption() {
    // Only update if scpiSelector exists (not in SCPI mode)
    if (!this.hasScpiSelectorTarget) return

    const scpiSelector = this.scpiSelectorTarget
    const customOption = scpiSelector.querySelector('option[value=""]')

    if (customOption) {
      customOption.disabled = true
    }
  }

  // Disable/enable custom parameters option based on mode
  updateParametresPersonnalisesOption(mode) {
    // Only update if scpiSelector exists (not in SCPI mode)
    if (!this.hasScpiSelectorTarget) return

    const scpiSelector = this.scpiSelectorTarget
    const customOption = scpiSelector.querySelector('option[value=""]')

    if (customOption) {
      if (mode === 'nue') {
        // Disable in nue-propriété mode
        customOption.disabled = true
        // If currently selected, reset to first SCPI
        if (scpiSelector.value === '') {
          const firstScpiOption = scpiSelector.querySelector('option[value]:not([value=""])')
          if (firstScpiOption) {
            scpiSelector.value = firstScpiOption.value
            // Trigger change event to load SCPI data
            scpiSelector.dispatchEvent(new Event('change'))
          }
        }
      } else {
        // Enable in pleine-propriété mode
        customOption.disabled = false
      }
    }
  }

  // DEPRECATED: This method is no longer used since we replaced radio buttons with a slider
  // Sync investment duration with bare ownership duration selection
  syncDurationFromNP(event) {
    // No longer needed - using npDurationSlider instead of radio buttons
    return
  }

  // Adapt duration slider minimum to bare ownership duration
  adaptDurationSliderToNueDurations(durations) {
    if (!durations || durations.length === 0) return

    // Sort durations
    const sortedDurations = [...durations].sort((a, b) => a - b)
    const minDuration = sortedDurations[0]

    // Set slider min to smallest démembrement, max stays at 25
    this.durationSliderTarget.min = minDuration
    this.durationSliderTarget.max = 25
    this.durationSliderTarget.step = 1

    // Update labels
    if (this.hasDurationMinLabelTarget) {
      this.durationMinLabelTarget.textContent = `${minDuration} années`
    }
    if (this.hasDurationMaxLabelTarget) {
      this.durationMaxLabelTarget.textContent = '25 années'
    }

    // If current duration < minimum, adjust to minimum
    const currentDuration = parseInt(this.durationInputTarget.value)
    if (currentDuration < minDuration) {
      this.durationSliderTarget.value = minDuration
      this.durationInputTarget.value = minDuration
      this.durationDisplayTarget.textContent = minDuration
    }
  }

  // Reset duration slider to default pleine-propriété mode
  resetDurationSliderToPleine() {
    this.durationSliderTarget.min = 3
    this.durationSliderTarget.max = 25
    this.durationSliderTarget.step = 1

    // Clear datalist
    if (this.hasDurationDatalistTarget) {
      this.durationDatalistTarget.innerHTML = ''
    }

    // Reset labels
    if (this.hasDurationMinLabelTarget) {
      this.durationMinLabelTarget.textContent = '3 années'
    }
    if (this.hasDurationMaxLabelTarget) {
      this.durationMaxLabelTarget.textContent = '25 années'
    }
  }

  // DEPRECATED: These methods are no longer used since we replaced radio buttons with a slider
  selectNPRadioByDuration(duration) {
    // No longer needed - using npDurationSlider instead of radio buttons
    return
  }

  updateNPRadioButtonsAvailability(investmentDuration) {
    // No longer needed - using npDurationSlider instead of radio buttons
    return
  }

  // Adjust current duration to nearest available value in bare ownership mode
  adjustDurationToAvailableValues() {
    if (!this.currentBareOwnershipDurations || this.currentBareOwnershipDurations.length === 0) {
      return
    }

    const currentDuration = parseInt(this.durationSliderTarget.value)
    const availableDurations = this.currentBareOwnershipDurations

    // Find the closest available duration
    const closestDuration = availableDurations.reduce((prev, curr) => {
      return Math.abs(curr - currentDuration) < Math.abs(prev - currentDuration) ? curr : prev
    })

    // Update slider value and display
    this.durationSliderTarget.value = closestDuration
    this.durationInputTarget.value = closestDuration
    this.durationDisplayTarget.textContent = closestDuration

    // Select corresponding radio button
    this.selectNPRadioByDuration(closestDuration)
  }

  applyTabStyles() {
    const mode = this.modeValue

    // Update tab styles only if tabs exist
    if (this.hasTabPleineTarget) {
      this.tabPleineTarget.classList.toggle("text-dark-blue", mode === "pleine")
      this.tabPleineTarget.classList.toggle("text-gray-500", mode !== "pleine")
      this.tabPleineTarget.classList.toggle("border-primary-green", mode === "pleine")
      this.tabPleineTarget.classList.toggle("border-transparent", mode !== "pleine")
    }

    // Only update tabNue if it exists (not present when locked_credit: true)
    if (this.hasTabNueTarget) {
      this.tabNueTarget.classList.toggle("text-dark-blue", mode === "nue")
      this.tabNueTarget.classList.toggle("text-gray-500", mode !== "nue")
      this.tabNueTarget.classList.toggle("border-primary-green", mode === "nue")
      this.tabNueTarget.classList.toggle("border-transparent", mode !== "nue")
    }

    // Show/hide relevant sections (only if nueSection exists)
    if (this.hasNueSectionTarget) {
      this.nueSectionTarget.classList.toggle("hidden", mode !== "nue")
    }

    // Show/hide duration sliders based on mode
    if (this.hasDurationInvestContainerTarget && this.hasNpDurationSliderContainerTarget) {
      if (mode === "nue") {
        // Nue mode: hide investment duration, show bare ownership duration slider
        this.durationInvestContainerTarget.classList.add("hidden")
        this.npDurationSliderContainerTarget.classList.remove("hidden")
      } else {
        // Pleine mode: show investment duration, hide bare ownership duration slider
        this.durationInvestContainerTarget.classList.remove("hidden")
        this.npDurationSliderContainerTarget.classList.add("hidden")
      }
    }

    // Show/hide results sections based on mode
    if (this.hasResultsTarget && this.hasNueResultsTarget) {
      if (mode === "nue") {
        this.resultsTarget.classList.add("hidden")
        this.nueResultsTarget.classList.remove("hidden")
      } else {
        this.nueResultsTarget.classList.add("hidden")
        this.resultsTarget.classList.remove("hidden")
      }
    }

    // Enable/disable SCPI options based on mode
    if (mode === "nue") {
      this.disableIneligibleScpiOptions()
    } else {
      this.enableAllScpiOptions()
    }

    // Adapt or reset duration slider based on mode
    if (mode === "nue" && this.currentBareOwnershipDurations) {
      this.adaptDurationSliderToNueDurations(this.currentBareOwnershipDurations)
    } else if (mode === "pleine") {
      this.resetDurationSliderToPleine()
    }

    // Show/hide IRR card (only in nue mode)
    if (this.hasIrrCardTarget) {
      this.irrCardTarget.classList.toggle("hidden", mode !== "nue")
    }

    // Hide credit section and toggle when in nue mode
    if (mode === "nue") {
      this.creditSectionTarget.classList.add("hidden")
      this.creditInfoTarget.classList.add("hidden")
      if (this.hasCreditToggleTarget) {
        this.creditToggleTarget.checked = false
        this.creditToggleTarget.closest('div').classList.add('hidden')
      }
    } else {
      // Show credit toggle in pleine mode
      if (this.hasCreditToggleTarget) {
        this.creditToggleTarget.closest('div').classList.remove('hidden')
      }
      // If locked_credit mode, ensure credit section and credit info are visible
      if (this.lockedCreditValue) {
        if (this.hasCreditSectionTarget) {
          this.creditSectionTarget.classList.remove('hidden')
        }
        if (this.hasCreditInfoTarget) {
          this.creditInfoTarget.classList.remove('hidden')
        }
      }
    }

    // IMPORTANT: In SCPI mode, rate slider ALWAYS stays disabled
    // No matter which tab is selected
    if (this.isScpiMode) {
      // Keep rate slider disabled permanently in SCPI mode
      this.setRateSliderState(true)
    } else {
      // In full mode, check if a SCPI is selected to manage rate slider state
      if (this.hasScpiSelectorTarget) {
        const scpiId = this.scpiSelectorTarget.value
        if (scpiId === "") {
          // Paramètres personnalisés - enable rate slider
          this.setRateSliderState(false)
        } else {
          // SCPI selected - disable rate slider
          this.setRateSliderState(true)
        }
      }
    }
  }

  // SCPI selection
  // Enable/disable the export button based on SCPI selection.
  // The "Paramètres personnalisés" option has an empty value: no real SCPI selected → button disabled.
  // In SCPI mode (no selector), a SCPI is always present → button enabled.
  updateExportButtonState() {
    if (!this.hasExportButtonTarget) return

    const noScpiSelected = this.hasScpiSelectorTarget && this.scpiSelectorTarget.value === ""
    this.exportButtonTargets.forEach((button) => {
      button.disabled = noScpiSelected
    })
  }

  selectScpi(event) {
    const scpiId = event ? event.target.value : this.scpiSelectorTarget.value
    this.updateExportButtonState()

    // Store last selected SCPI if it's not custom parameters
    if (scpiId !== "") {
      this.lastSelectedScpiId = scpiId
      // Re-enable inputs if they were disabled
      if (this.modeValue === 'nue') {
        this.disableAllInputs(false)
        // Remove blur from all sections
        if (this.hasResultsTarget) {
          this.resultsTarget.classList.remove('opacity-50', 'blur-sm')
        }
        if (this.hasNueResultsTarget) {
          this.nueResultsTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
        }
        if (this.hasNueSectionTarget) {
          this.nueSectionTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
        }
        if (this.hasSlidersSectionTarget) {
          this.slidersSectionTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
        }
        if (this.hasActionButtonsTarget) {
          this.actionButtonsTarget.classList.remove('opacity-50', 'blur-sm', 'pointer-events-none')
        }
        // Hide ineligible warning when selecting valid SCPI
        this.hideIneligibleWarning()
      }
    }

    // En mode SCPI, on ne touche jamais au taux (il reste toujours désactivé)
    if (this.isScpiMode) {
      this.calculate()
      return
    }

    if (scpiId === "") {
      // Custom parameters - enable sliders (unless in nue-propriété mode)
      if (this.modeValue !== "nue") {
        this.setRateSliderState(false)
      }
      // Reset N/A flag and hide tooltip
      this.rateNotAvailable = false
      if (this.hasRateNaTooltipTarget) {
        this.rateNaTooltipTarget.classList.add('hidden')
      }
      // Reset to default label, default delay (6 months), and default frequency
      this.incomeLabelTarget.textContent = "Acompte sur distribution mensuel potentiel"
      this.currentEnjoymentDelay = 6
      this.currentPaymentFrequency = "Mensuelle"

      // Load default bare ownership durations
      this.loadBareOwnershipDurations({
        bare_ownership_durations: [5, 10, 15, 20],
        bare_ownership_possible: true
      })
    } else {
      // Load SCPI data
      const scpi = this.scpisValue.find(s => s.id == scpiId)
      if (scpi) {
        const rate = parseFloat(scpi.distribution_rate)

        // If distribution rate is not available, lock slider and show N/A indicator
        if (isNaN(rate) || !scpi.distribution_rate) {
          // Use target_irr if available, otherwise default to 7%
          const targetIrr = parseFloat(scpi.target_irr)
          const defaultRate = isNaN(targetIrr) ? 7.0 : targetIrr
          this.rateNotAvailable = true
          this.rateSliderTarget.value = defaultRate
          this.rateInputTarget.value = defaultRate
          this.rateDisplayTarget.textContent = defaultRate.toFixed(2).replace('.', ',') + ' %'
          // Show tooltip for N/A indicator
          if (this.hasRateNaTooltipTarget) {
            this.rateNaTooltipTarget.classList.remove('hidden')
          }
          this.setRateSliderState(true) // Lock slider
        } else {
          this.rateNotAvailable = false
          this.rateSliderTarget.value = rate
          this.rateInputTarget.value = rate
          this.rateDisplayTarget.textContent = rate.toFixed(2).replace('.', ',') + ' %'
          // Hide tooltip for N/A indicator
          if (this.hasRateNaTooltipTarget) {
            this.rateNaTooltipTarget.classList.add('hidden')
          }
          this.setRateSliderState(true)
        }

        // Store payment frequency
        this.currentPaymentFrequency = scpi.payment_frequency || "Mensuelle"

        // Update income label based on payment frequency
        const frequency = scpi.payment_frequency || "Mensuelle"
        if (frequency === "Trimestrielle") {
          this.incomeLabelTarget.textContent = "Acompte sur distribution trimestriel potentiel"
        } else {
          this.incomeLabelTarget.textContent = "Acompte sur distribution mensuel potentiel"
        }

        // Store enjoyment delay for calculations (+1 month as per client requirements)
        this.currentEnjoymentDelay = (parseInt(scpi.enjoyment_delay) || 0) + 1

        // Load bare ownership durations
        this.loadBareOwnershipDurations(scpi)
      }
    }

    this.calculate()
  }

  // Slider updates
  updateAmount(event) {
    const value = parseInt(event.target.value)
    this.amountInputTarget.value = value
    this.amountDisplayTarget.value = this.formatNumber(value)

    // Update apport max to match investment amount
    this.updateApportMax(value)

    this.calculate()
  }

  // Manual input from text input (with formatting)
  updateAmountFromInput(event) {
    // Parse value by removing spaces
    const rawValue = event.target.value.replace(/\s/g, '')
    const value = parseInt(rawValue) || 100000

    // Clamp value between min and max
    const clampedValue = Math.max(1000, Math.min(500000, value))

    this.amountInputTarget.value = clampedValue
    this.amountSliderTarget.value = clampedValue
    this.amountDisplayTarget.value = this.formatNumber(clampedValue)

    // Update apport max to match investment amount
    this.updateApportMax(clampedValue)

    this.calculate()
  }

  // Select all text on focus for easy editing
  selectAmountInput(event) {
    event.target.select()
  }

  // Format number with spaces for thousands
  formatNumber(value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  // Prevent Enter key from submitting form
  preventEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.target.blur() // Remove focus to trigger change event
    }
  }

  updateRate(event) {
    const value = parseFloat(event.target.value)
    this.rateInputTarget.value = value
    // Display rate value without N/A indicator in text (tooltip handles it)
    this.rateDisplayTarget.textContent = value.toFixed(2).replace('.', ',') + ' %'
    this.calculate()
  }

  updateDuration(event) {
    let value = parseInt(event.target.value)

    this.durationInputTarget.value = value
    this.durationDisplayTarget.textContent = value

    this.calculate()
  }

  updateReval(event) {
    const value = parseFloat(event.target.value)
    this.revalInputTarget.value = value
    this.revalDisplayTarget.textContent = value.toFixed(1).replace('.', ',')
    this.calculate()
  }

  // Update nue-propriété duration from slider
  updateNpDuration(event) {
    const value = parseInt(event.target.value)

    // In nue mode, snap to closest available duration
    if (this.currentBareOwnershipDurations && this.currentBareOwnershipDurations.length > 0) {
      // Find the closest available duration
      const closestDuration = this.currentBareOwnershipDurations.reduce((prev, curr) => {
        return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
      })

      // Update slider and display to snapped value
      this.npDurationSliderTarget.value = closestDuration
      this.npDurationInputTarget.value = closestDuration
      this.npDurationDisplayTarget.textContent = closestDuration
    } else {
      // Fallback: just use the value as-is
      this.npDurationInputTarget.value = value
      this.npDurationDisplayTarget.textContent = value
    }

    this.calculate()
  }

  // Update apport max based on investment amount
  updateApportMax(investmentAmount) {
    if (!this.hasApportSliderTarget) return

    // Set apport max to investment amount
    this.apportSliderTarget.max = investmentAmount

    // Update label
    if (this.hasApportMaxLabelTarget) {
      this.apportMaxLabelTarget.textContent = this.formatNumber(investmentAmount) + ' €'
    }

    // If current apport exceeds new max, adjust it
    const currentApport = parseInt(this.apportInputTarget.value)
    if (currentApport > investmentAmount) {
      this.apportSliderTarget.value = investmentAmount
      this.apportInputTarget.value = investmentAmount
      this.apportDisplayTarget.textContent = investmentAmount.toLocaleString('fr-FR')
    }
  }

  // Credit sliders
  updateApport(event) {
    const value = parseInt(event.target.value)
    this.apportInputTarget.value = value
    this.apportDisplayTarget.textContent = value.toLocaleString('fr-FR')
    this.calculate()
  }

  updateLoanDuration(event) {
    const value = parseInt(event.target.value)
    this.loanDurationInputTarget.value = value
    this.loanDurationDisplayTarget.textContent = value
    this.calculate()
  }

  updateLoanRate(event) {
    const value = parseFloat(event.target.value)
    this.loanRateInputTarget.value = value
    this.loanRateDisplayTarget.textContent = value.toFixed(2).replace('.', ',')
    this.calculate()
  }

  // Credit toggle
  toggleCredit(event) {
    const isCredit = event.target.checked
    this.creditSectionTarget.classList.toggle('hidden', !isCredit)
    this.creditInfoTarget.classList.toggle('hidden', !isCredit)
    this.calculate()
  }

  // Load bare ownership durations dynamically
  loadBareOwnershipDurations(scpi) {
    if (!scpi) return

    // Check if bare ownership is available for this SCPI
    // Check both bare_ownership_possible AND eligible_bare_ownership from simulator_parameter
    const bareOwnershipPossible = scpi.bare_ownership_possible !== false &&
                                    (scpi.simulator_parameter?.eligible_bare_ownership !== false)

    if (!bareOwnershipPossible) {
      // Store that this SCPI doesn't support bare ownership
      this.currentBareOwnershipPossible = false

      // If we're currently in nue mode, disable all inputs and show warning
      if (this.modeValue === 'nue') {
        this.disableAllInputs(true)
        this.showIneligibleWarning("Cette SCPI n'est pas éligible au démembrement de propriété")
      }
      return
    }

    // Re-enable the section if it was previously disabled
    if (this.hasNueSectionTarget) {
      this.nueSectionTarget.classList.remove('opacity-50', 'pointer-events-none')
    }
    this.currentBareOwnershipPossible = true

    // Re-enable inputs if they were disabled
    this.disableAllInputs(false)

    // Hide warning if it was displayed
    this.hideIneligibleWarning()

    // Get durations and discounts from SCPI data
    const durations = scpi.bare_ownership_durations && scpi.bare_ownership_durations.length > 0
      ? scpi.bare_ownership_durations
      : [5, 10, 15, 20]

    const discounts = scpi.simulator_parameter?.bare_ownership_discounts_by_duration || {
      "5": 20,
      "10": 30,
      "15": 40,
      "20": 50
    }

    // Store discounts for calculation
    this.currentBareOwnershipDiscounts = discounts

    // Store available durations for slider adaptation
    this.currentBareOwnershipDurations = durations

    // Configure NP duration slider if it exists
    if (this.hasNpDurationSliderTarget) {
      const sortedDurations = [...durations].sort((a, b) => a - b)
      const minDuration = sortedDurations[0]
      const maxDuration = sortedDurations[sortedDurations.length - 1]

      // Set slider range to available durations
      this.npDurationSliderTarget.min = minDuration
      this.npDurationSliderTarget.max = maxDuration
      this.npDurationSliderTarget.step = 1 // Step of 1, but we'll snap to available values in updateNpDuration

      // Set initial value to first available duration or current value if valid
      const currentValue = parseInt(this.npDurationInputTarget.value) || minDuration
      const initialValue = durations.includes(currentValue) ? currentValue : minDuration
      this.npDurationSliderTarget.value = initialValue
      this.npDurationInputTarget.value = initialValue
      this.npDurationDisplayTarget.textContent = initialValue

      // Update labels
      if (this.hasNpDurationMinLabelTarget) {
        this.npDurationMinLabelTarget.textContent = `${minDuration} années`
      }
      if (this.hasNpDurationMaxLabelTarget) {
        this.npDurationMaxLabelTarget.textContent = `${maxDuration} années`
      }
    }
  }

  // Main calculation
  calculate() {
    const amount = parseInt(this.amountInputTarget.value) || 20000
    const rate = (parseFloat(this.rateInputTarget.value) || 7.0) / 100
    const duration = parseInt(this.durationInputTarget.value) || 10
    const reval = (parseFloat(this.revalInputTarget.value) || 0) / 100

    // Always calculate nue-propriété values if targets exist (for pre-loading values)
    if (this.hasNueResultsTarget) {
      this.calculateNue(amount, rate, duration, reval)
    }

    // Calculate based on active mode
    if (this.modeValue === "nue") {
      // Nue already calculated above
    } else {
      // Mode pleine : check if credit is enabled via toggle
      const isCredit = this.hasCreditToggleTarget && this.creditToggleTarget.checked
      if (isCredit) {
        this.calculateCredit(amount, rate, duration, reval)
      } else {
        this.calculatePleine(amount, rate, duration, reval)
      }
    }
  }

  calculatePleine(amount, rate, duration, reval) {
    // Calculate periodic income based on payment frequency
    const isQuarterly = this.currentPaymentFrequency === "Trimestrielle"
    const periodsPerYear = isQuarterly ? 4 : 12
    const periodicIncome = (amount * rate) / periodsPerYear

    // Calculate total income minus enjoyment delay
    const delayMonths = this.currentEnjoymentDelay || 0
    // Convert delay to periods (quarters if quarterly, months if monthly)
    const delayPeriods = isQuarterly ? Math.ceil(delayMonths / 3) : delayMonths
    const totalPeriods = (periodsPerYear * duration) - delayPeriods
    const totalIncome = periodicIncome * totalPeriods

    // Final value with revalorisation
    const finalValue = amount * Math.pow(1 + reval, duration)
    const capitalGain = finalValue - amount

    // Total gain
    const totalGain = totalIncome + capitalGain

    // Update display
    this.monthlyIncomeTarget.textContent = Math.round(periodicIncome).toLocaleString('fr-FR')
    this.totalIncomeTarget.textContent = Math.round(totalIncome).toLocaleString('fr-FR')
    this.finalValueTarget.textContent = Math.round(finalValue).toLocaleString('fr-FR')
    this.capitalGainTarget.textContent = Math.round(capitalGain).toLocaleString('fr-FR')
    this.totalGainTarget.textContent = Math.round(totalGain).toLocaleString('fr-FR')

    // Update label for total gain (comptant mode)
    if (this.hasTotalGainLabelTarget) {
      this.totalGainLabelTarget.textContent = "Revenus + capital"
    }
  }

  calculateCredit(amount, rate, duration, reval) {
    // Get credit parameters
    const apport = parseInt(this.apportInputTarget.value) || 0
    const loanAmount = amount - apport
    const loanDuration = parseInt(this.loanDurationInputTarget.value)
    const loanRate = parseFloat(this.loanRateInputTarget.value) / 100

    // Calculate monthly payment
    const monthlyRate = loanRate / 12
    const n = loanDuration * 12
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)

    // Total credit cost
    const totalPaid = monthlyPayment * n
    const creditCost = totalPaid - loanAmount

    // Income calculations with enjoyment delay and payment frequency
    const isQuarterly = this.currentPaymentFrequency === "Trimestrielle"
    const periodsPerYear = isQuarterly ? 4 : 12
    const periodicIncome = (amount * rate) / periodsPerYear
    const delayMonths = this.currentEnjoymentDelay || 0
    // Convert delay to periods (quarters if quarterly, months if monthly)
    const delayPeriods = isQuarterly ? Math.ceil(delayMonths / 3) : delayMonths
    const totalPeriods = (periodsPerYear * duration) - delayPeriods
    const totalIncome = periodicIncome * totalPeriods
    const finalValue = amount * Math.pow(1 + reval, duration)
    const capitalGain = finalValue - amount

    // Net gain after credit cost
    const totalGain = totalIncome + capitalGain - creditCost

    // Update display
    this.monthlyIncomeTarget.textContent = Math.round(periodicIncome).toLocaleString('fr-FR')
    this.totalIncomeTarget.textContent = Math.round(totalIncome).toLocaleString('fr-FR')
    this.finalValueTarget.textContent = Math.round(finalValue).toLocaleString('fr-FR')
    this.capitalGainTarget.textContent = Math.round(capitalGain).toLocaleString('fr-FR')
    this.totalGainTarget.textContent = Math.round(totalGain).toLocaleString('fr-FR')

    // Update label for total gain (credit mode)
    if (this.hasTotalGainLabelTarget) {
      this.totalGainLabelTarget.textContent = "Revenus + capital - Coût de l'emprunt"
    }

    // Credit info
    this.loanAmountTarget.textContent = loanAmount.toLocaleString('fr-FR')
    this.monthlyPaymentTarget.textContent = Math.round(monthlyPayment).toLocaleString('fr-FR')
    this.creditCostTarget.textContent = Math.round(creditCost).toLocaleString('fr-FR')
  }

  calculateNue(amount, rate, duration, reval) {
    // Get nue-propriété duration from slider (not radio buttons anymore)
    const npDuration = this.hasNpDurationInputTarget
      ? parseInt(this.npDurationInputTarget.value)
      : 10

    // Get discount from stored data (dynamic) or fallback to defaults
    const discounts = this.currentBareOwnershipDiscounts || {
      "5": 20,
      "10": 30,
      "15": 40,
      "20": 50
    }
    const discountPercent = discounts[npDuration.toString()] || 30

    // Get current SCPI data for share price
    let sharePrice = 0
    if (this.isScpiMode) {
      // In SCPI mode, get share price from preloaded data
      sharePrice = parseFloat(this.scpiPreloadedTarget.dataset.sharePrice) || 0
    } else {
      // In full mode, get share price from selected SCPI
      const scpiId = this.scpiSelectorTarget.value
      const scpi = scpiId ? this.scpisValue.find(s => s.id == scpiId) : null
      sharePrice = scpi ? parseFloat(scpi.share_price) || 0 : 0
    }

    // Calculate bare ownership metrics
    const nueSharePrice = sharePrice * (1 - discountPercent / 100)
    const nueRate = 100 - discountPercent
    const nueInitialAmount = amount // L'investissement initial = montant saisi en nue-propriété

    // Calculate full property equivalent (what the bare ownership will be worth at the end)
    // If you pay 150 000€ in bare ownership at 70% (30% discount), you're buying the equivalent of 214 286€ in full ownership
    const fullPropertyEquivalent = amount / (nueRate / 100)
    const nueFinalValue = fullPropertyEquivalent * Math.pow(1 + reval, npDuration) // Full property value with revalorisation
    const nueGain = nueFinalValue - nueInitialAmount

    // Calculate IRR (Internal Rate of Return)
    const irr = this.calculateIRR(nueInitialAmount, 0, nueFinalValue, npDuration, npDuration)

    // Update display
    if (this.hasNueSharePriceTarget) {
      this.nueSharePriceTarget.textContent = Math.round(nueSharePrice).toLocaleString('fr-FR')
    }
    if (this.hasNueRateTarget) {
      this.nueRateTarget.textContent = nueRate.toFixed(1).replace('.', ',')
    }
    if (this.hasNueInitialAmountTarget) {
      this.nueInitialAmountTarget.textContent = Math.round(nueInitialAmount).toLocaleString('fr-FR')
    }
    if (this.hasNueFinalValueTarget) {
      this.nueFinalValueTarget.textContent = Math.round(nueFinalValue).toLocaleString('fr-FR')
    }
    if (this.hasNueGainTarget) {
      this.nueGainTarget.textContent = Math.round(nueGain).toLocaleString('fr-FR')
    }
    if (this.hasNueIrrTarget) {
      this.nueIrrTarget.textContent = irr.toFixed(2).replace('.', ',')
    }
    if (this.hasIrrDisplayTarget) {
      this.irrDisplayTarget.textContent = irr.toFixed(2).replace('.', ',')
    }
  }

  // Calculate Internal Rate of Return (IRR/TRI) using Newton's method
  calculateIRR(initialInvestment, totalIncome, finalValue, duration, npDuration) {
    // Build cash flows array
    const cashFlows = []

    // Year 0: negative cash flow (initial investment)
    cashFlows[0] = -initialInvestment

    // Years 1 to npDuration: no cash flow (démembrement period)
    for (let i = 1; i <= npDuration; i++) {
      cashFlows[i] = 0
    }

    // Years after npDuration: annual income
    const annualIncome = totalIncome / Math.max(1, duration - npDuration)
    for (let i = npDuration + 1; i < duration; i++) {
      cashFlows[i] = annualIncome
    }

    // Final year: income + final value
    cashFlows[duration] = annualIncome + finalValue

    // Newton's method to find IRR
    let irr = 0.1 // Initial guess: 10%
    const maxIterations = 100
    const precision = 0.00001

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let npv = 0
      let npvDerivative = 0

      for (let i = 0; i <= duration; i++) {
        const discountFactor = Math.pow(1 + irr, i)
        npv += cashFlows[i] / discountFactor
        npvDerivative += (-i * cashFlows[i]) / (discountFactor * (1 + irr))
      }

      const newIrr = irr - npv / npvDerivative

      if (Math.abs(newIrr - irr) < precision) {
        return newIrr * 100 // Return as percentage
      }

      irr = newIrr
    }

    return irr * 100 // Return as percentage even if not fully converged
  }

  // Share functionality
  share() {
    // Open lead modal for sharing
    const event = new CustomEvent('open-lead-modal', { detail: { action: 'share' } })
    window.dispatchEvent(event)
  }

  // Export simulation
  exportSimulation() {
    // Collect all simulation data
    const simulationData = {
      mode: this.modeValue,
      investmentAmount: parseInt(this.amountInputTarget.value),
      amount: parseInt(this.amountInputTarget.value),
      rate: parseFloat(this.rateInputTarget.value),
      duration: parseInt(this.durationInputTarget.value),
      reval: parseFloat(this.revalInputTarget.value),
      results: {
        monthlyIncome: this.monthlyIncomeTarget.textContent,
        totalIncome: this.totalIncomeTarget.textContent,
        finalValue: this.finalValueTarget.textContent,
        capitalGain: this.capitalGainTarget.textContent,
        totalGain: this.totalGainTarget.textContent
      }
    }

    // Add credit data if applicable
    if (this.hasCreditToggleTarget && this.creditToggleTarget.checked) {
      simulationData.credit = {
        apport: parseInt(this.apportInputTarget.value),
        loanDuration: parseInt(this.loanDurationInputTarget.value),
        loanRate: parseFloat(this.loanRateInputTarget.value),
        loanAmount: this.loanAmountTarget.textContent,
        monthlyPayment: this.monthlyPaymentTarget.textContent,
        creditCost: this.creditCostTarget.textContent
      }
    }

    // Add bare ownership data if applicable
    if (this.modeValue === 'nue') {
      const npDuration = this.hasNpDurationInputTarget ? parseInt(this.npDurationInputTarget.value) : 10

      simulationData.bareOwnership = {
        duration: npDuration,
        irr: this.hasIrrDisplayTarget ? this.irrDisplayTarget.textContent : '0,00',
        sharePrice: this.hasNueSharePriceTarget ? this.nueSharePriceTarget.textContent : '0',
        rate: this.hasNueRateTarget ? this.nueRateTarget.textContent : '0',
        initialAmount: this.hasNueInitialAmountTarget ? this.nueInitialAmountTarget.textContent : '0',
        finalValue: this.hasNueFinalValueTarget ? this.nueFinalValueTarget.textContent : '0',
        gain: this.hasNueGainTarget ? this.nueGainTarget.textContent : '0'
      }
    }

    // Add SCPI name if available
    if (this.isScpiMode && this.hasScpiPreloadedTarget) {
      // Mode SCPI: get SCPI name from preloaded data or scpisValue
      const scpiId = this.scpiPreloadedTarget.dataset.scpiId
      const scpi = this.scpisValue[0] // In SCPI mode, there's only one SCPI
      if (scpi) {
        simulationData.scpiName = scpi.name
      }
    } else if (this.hasScpiSelectorTarget && this.scpiSelectorTarget.value) {
      // Mode full: get from selector
      const selectedOption = this.scpiSelectorTarget.options[this.scpiSelectorTarget.selectedIndex]
      simulationData.scpiName = selectedOption.text
    }

    // Store data in localStorage for the export
    localStorage.setItem('pendingSimulationExport', JSON.stringify(simulationData))

    // Open lead modal for email capture
    const event = new CustomEvent('open-lead-modal', {
      detail: { action: 'export_simulation' }
    })
    window.dispatchEvent(event)
  }

  // Enable or disable rate slider with visual feedback
  setRateSliderState(disabled) {
    // Get container via target or fallback to closest
    const container = this.hasRateContainerTarget
      ? this.rateContainerTarget
      : this.rateSliderTarget.closest('.field-container')

    if (!container) {
      console.warn('Rate container not found')
      return
    }

    // Force style application with requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Set disabled state on slider with both property AND attribute
      this.rateSliderTarget.disabled = disabled
      if (disabled) {
        this.rateSliderTarget.setAttribute('disabled', 'disabled')
        container.classList.add('opacity-50', 'pointer-events-none')
        this.rateSliderTarget.style.cursor = 'not-allowed'
      } else {
        this.rateSliderTarget.removeAttribute('disabled')
        container.classList.remove('opacity-50', 'pointer-events-none')
        this.rateSliderTarget.style.cursor = ''
      }
    })
  }

  // Disable or enable all input sliders
  disableAllInputs(disable) {
    const sliders = [
      this.amountSliderTarget,
      this.rateSliderTarget,
      this.durationSliderTarget,
      this.revalSliderTarget
    ]

    const inputs = [
      this.amountInputTarget,
      this.rateInputTarget,
      this.durationInputTarget,
      this.revalInputTarget
    ]

    sliders.forEach(slider => {
      slider.disabled = disable
      if (disable) {
        slider.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      } else {
        slider.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      }
    })

    inputs.forEach(input => {
      input.disabled = disable
      if (disable) {
        input.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      } else {
        input.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      }
    })

    // Also disable/enable parent containers for visual feedback
    const parentContainers = sliders.map(s => s.closest('.field-container') || s.closest('div'))
    parentContainers.forEach(container => {
      if (container) {
        if (disable) {
          container.classList.add('opacity-50', 'pointer-events-none')
        } else {
          container.classList.remove('opacity-50', 'pointer-events-none')
        }
      }
    })
  }

  showPlaceholderResults() {
    // Show "---" or blur results when no SCPI is selected in nue mode
    const resultElements = [
      this.monthlyIncomeTarget,
      this.totalIncomeTarget,
      this.finalValueTarget,
      this.capitalGainTarget,
      this.totalGainTarget
    ]

    resultElements.forEach(el => {
      if (el) {
        el.textContent = '---'
      }
    })

    // Also blur/hide IRR display if exists
    if (this.hasIrrDisplayTarget) {
      this.irrDisplayTarget.textContent = '---'
    }

    // Add blur effect to results container (pleine-propriété)
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add('opacity-50', 'blur-sm')
    }

    // Add blur effect to nue-propriété results
    if (this.hasNueResultsTarget) {
      this.nueResultsTarget.classList.add('opacity-50', 'blur-sm', 'pointer-events-none')
    }

    // Blur and disable nue-propriété parameters section
    if (this.hasNueSectionTarget) {
      this.nueSectionTarget.classList.add('opacity-50', 'blur-sm', 'pointer-events-none')
    }

    // Blur sliders section
    if (this.hasSlidersSectionTarget) {
      this.slidersSectionTarget.classList.add('opacity-50', 'blur-sm', 'pointer-events-none')
    }

    // Blur action buttons
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.add('opacity-50', 'blur-sm', 'pointer-events-none')
    }
  }

  showIneligibleWarning(message) {
    if (this.hasIneligibleWarningTarget) {
      this.ineligibleWarningTarget.classList.remove('hidden')
      if (this.hasIneligibleMessageTarget) {
        this.ineligibleMessageTarget.textContent = message
      }
    }
  }

  hideIneligibleWarning() {
    if (this.hasIneligibleWarningTarget) {
      this.ineligibleWarningTarget.classList.add('hidden')
    }
  }

  disableIneligibleScpiOptions() {
    if (!this.hasScpiSelectorTarget || this.isScpiMode) return

    const scpiSelector = this.scpiSelectorTarget
    const options = scpiSelector.querySelectorAll('option[value]:not([value=""])')

    options.forEach(option => {
      const scpiId = parseInt(option.value)
      const scpi = this.scpisValue.find(s => s.id === scpiId)

      if (scpi && scpi.bare_ownership_possible === false) {
        option.disabled = true
        option.textContent = `${scpi.name} (non disponible en nue-propriété)`
      }
    })
  }

  enableAllScpiOptions() {
    if (!this.hasScpiSelectorTarget || this.isScpiMode) return

    const scpiSelector = this.scpiSelectorTarget
    const options = scpiSelector.querySelectorAll('option[value]:not([value=""])')

    options.forEach(option => {
      const scpiId = parseInt(option.value)
      const scpi = this.scpisValue.find(s => s.id === scpiId)

      if (scpi && option.disabled) {
        option.disabled = false
        option.textContent = scpi.name
      }
    })
  }

  // Warning modal methods
  showWarningModal(event) {
    event.preventDefault()
    if (this.hasWarningModalTarget) {
      this.warningModalTarget.classList.remove('hidden')
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
  }

  hideWarningModal(event) {
    event.preventDefault()
    if (this.hasWarningModalTarget) {
      this.warningModalTarget.classList.add('hidden')
      // Restore body scroll
      document.body.style.overflow = ''
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}